/*
  SOLVERA third-party DOM repairs (deferred; watches app embeds as they
  inject and fixes semantic/a11y/SEO defects in their markup).

  - <a href="javascript:..."> triggers (WhatsApp share/chat widgets): fail
    Lighthouse "crawlable-anchors" and are semantically buttons. Removing the
    href keeps the apps' own click handlers working.
  - iframes without titles (Google merchant widget): fail "frame-title".
  - role="dialog" without an accessible name (Shopify Forms popup): labelled
    from its own heading when available.

  The observer disconnects after 120s. It was 20s, but under Lighthouse's
  4x mobile CPU throttle the chat widget and Shopify Forms popup inject
  AFTER 20s, resurfacing the javascript: anchor (SEO crawlable-anchors,
  home+PDP 92) and the unnamed dialog — measured live 2026-07-11. A quiet
  childList observer costs nothing measurable while it waits.
*/
(() => {
  const repair = (root) => {
    const scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll('a[href^="javascript:"]').forEach((anchor) => {
      anchor.removeAttribute('href');
      anchor.setAttribute('role', 'button');
      if (!anchor.hasAttribute('tabindex')) anchor.setAttribute('tabindex', '0');
      anchor.style.cursor = 'pointer';
    });

    // Href-less <a> used as interactive controls (Judge.me renders its
    // write-review rating stars this way): they're buttons semantically,
    // and unroled they fail Lighthouse "crawlable-anchors" on every PDP.
    scope.querySelectorAll('a:not([href]):not([role])').forEach((anchor) => {
      anchor.setAttribute('role', 'button');
    });

    // Safety net for article bodies pasted from VS Code's markdown preview:
    // root-relative links got rewritten to the preview's own host
    // (file+.vscode-resource.vscode-cdn.net/...), 404ing for customers.
    // Rewrite to the intended same-store path. The REAL fix is correcting
    // the article HTML in admin (crawlers see raw HTML, not this rewrite) —
    // see docs/seo-crawl-2026-07.md; remove this block once content is clean.
    scope.querySelectorAll('a[href*="vscode-resource"], a[href*="vscode-cdn"]').forEach((anchor) => {
      try {
        anchor.setAttribute('href', new URL(anchor.getAttribute('href')).pathname);
      } catch (error) {
        /* leave unparseable hrefs untouched */
      }
    });

    scope.querySelectorAll('iframe:not([title]):not([aria-label])').forEach((frame) => {
      frame.setAttribute('title', frame.id || frame.name || 'Embedded content');
    });

    scope.querySelectorAll('[role="dialog"]:not([aria-label]):not([aria-labelledby])').forEach((dialog) => {
      const heading = dialog.querySelector('h1, h2, h3, [class*="title" i]');
      const label = heading && heading.textContent.trim();
      dialog.setAttribute('aria-label', label ? label.slice(0, 80) : 'Dialog');
    });
  };

  const start = () => {
    repair(document);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 1) repair(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      repair(document);
      observer.disconnect();
    }, 120000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
